import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle, Plus, Trash2, Save, ArrowLeft, Copy, Sparkles, Filter,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const PIPELINE_META = [
  { key: "b2c_jaydiets", label: "JAYDIETs", accent: "bg-emerald-500", ringed: "border-emerald-500/40" },
  { key: "b2c_coffee",   label: "Coffee",   accent: "bg-amber-500",  ringed: "border-amber-500/40" },
  { key: "b2c_wellness", label: "Wellness", accent: "bg-fuchsia-500",ringed: "border-fuchsia-500/40" },
  { key: "b2c_retreat",  label: "Retreat",  accent: "bg-sky-500",   ringed: "border-sky-500/40" },
  { key: "b2b_growth",   label: "B2B Growth", accent: "bg-orange-500", ringed: "border-orange-500/40" },
];

const EMPTY = { name: "", pipeline: "b2c_jaydiets", body: "", sequence_step: null };

export default function WhatsAppTemplates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pipelineFilter, setPipelineFilter] = useState("");
  const [editing, setEditing] = useState(null);   // null | EMPTY (new) | template obj (edit)

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/templates", {
        params: { pipeline: pipelineFilter || undefined },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load templates");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pipelineFilter]);

  const save = async () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.body.trim()) {
      toast.error("Name and body are required."); return;
    }
    try {
      if (editing.id) {
        const { id, ...rest } = editing;
        const { data } = await api.patch(`/whatsapp/templates/${id}`, rest);
        setItems((prev) => prev.map((t) => (t.id === id ? data : t)));
        toast.success("Template updated");
      } else {
        const { data } = await api.post("/whatsapp/templates", editing);
        setItems((prev) => [data, ...prev]);
        toast.success("Template created");
      }
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/whatsapp/templates/${id}`);
      setItems((prev) => prev.filter((t) => t.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  const grouped = useMemo(() => {
    const g = {};
    PIPELINE_META.forEach((p) => (g[p.key] = []));
    items.forEach((t) => {
      if (!g[t.pipeline]) g[t.pipeline] = [];
      g[t.pipeline].push(t);
    });
    return g;
  }, [items]);

  return (
    <div className="space-y-8" data-testid="whatsapp-templates-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-primary mb-2">Parent dashboard · WhatsApp</div>
          <h1 className="font-display text-4xl leading-tight">Quick-reply templates.</h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            Save follow-up nudges per pipeline. Placeholders like
            <code className="mx-1 rounded bg-secondary px-1.5 py-0.5 text-xs">[First Name]</code>,
            <code className="mx-1 rounded bg-secondary px-1.5 py-0.5 text-xs">[Company Name]</code>,
            <code className="mx-1 rounded bg-secondary px-1.5 py-0.5 text-xs">[City]</code> auto-resolve per lead
            when the template is sent.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/admin/whatsapp"><Button variant="ghost" className="rounded-full gap-1.5"><ArrowLeft className="h-3.5 w-3.5" /> Back to Log</Button></Link>
          <Button onClick={() => setEditing({ ...EMPTY })} className="rounded-full gap-1.5" data-testid="wa-tpl-new-btn">
            <Plus className="h-4 w-4" /> New template
          </Button>
        </div>
      </div>

      {/* Pipeline filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-3.5 w-3.5 text-muted-foreground" />
        <button
          onClick={() => setPipelineFilter("")}
          data-testid="wa-tpl-filter-all"
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs uppercase tracking-[0.18em] transition-colors ${pipelineFilter === "" ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
        >
          All
        </button>
        {PIPELINE_META.map((p) => (
          <button
            key={p.key}
            onClick={() => setPipelineFilter(p.key)}
            data-testid={`wa-tpl-filter-${p.key}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs uppercase tracking-[0.18em] transition-colors ${pipelineFilter === p.key ? `${p.ringed} bg-secondary text-foreground` : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            <span className={"h-2 w-2 rounded-full " + p.accent} />
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-sm text-muted-foreground" data-testid="wa-tpl-empty">
          No templates yet. Click <b>New template</b> to save your first one.
        </div>
      ) : (
        <div className="space-y-8">
          {PIPELINE_META.filter((p) => (grouped[p.key] || []).length > 0).map((p) => (
            <div key={p.key} data-testid={`wa-tpl-section-${p.key}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={"h-2.5 w-2.5 rounded-full " + p.accent} />
                <h2 className="font-display text-xl">{p.label}</h2>
                <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground ml-1">
                  {grouped[p.key].length} template{grouped[p.key].length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {grouped[p.key].map((t) => (
                  <div key={t.id} className={"rounded-xl border bg-card p-4 group " + p.ringed} data-testid={`wa-tpl-card-${t.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="text-sm font-medium truncate">{t.name}</div>
                      {t.is_seed && (
                        <span className="rounded-full bg-primary/15 text-primary text-[10px] px-1.5 py-0.5 uppercase tracking-[0.18em]">Seed</span>
                      )}
                    </div>
                    {t.sequence_step != null && (
                      <div className="text-[11px] text-muted-foreground mb-2">
                        Day {t.sequence_step}
                      </div>
                    )}
                    <div className="text-xs text-foreground/90 whitespace-pre-wrap min-h-[80px] leading-relaxed">
                      {t.body}
                    </div>
                    <div className="mt-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="sm" variant="ghost" className="h-7 rounded-full text-xs" onClick={() => setEditing(t)} data-testid={`wa-tpl-edit-${t.id}`}>Edit</Button>
                      <Button size="sm" variant="ghost" className="h-7 rounded-full text-xs" onClick={() => { navigator.clipboard.writeText(t.body); toast.success("Body copied"); }}>
                        <Copy className="h-3 w-3" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-7 rounded-full text-xs text-destructive hover:text-destructive" data-testid={`wa-tpl-delete-${t.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this template?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This can't be undone. Seed templates re-appear on next page reload.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(t.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`wa-tpl-delete-confirm-${t.id}`}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-background border border-border rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wa-tpl-editor">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-2xl">{editing.id ? "Edit template" : "New template"}</div>
              <button onClick={() => setEditing(null)} className="p-1 rounded hover:bg-foreground/10"><Plus className="h-4 w-4 rotate-45" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Template name *</Label>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. JAYDIETs · Day 5 · Trial reminder"
                  className="mt-1.5"
                  data-testid="wa-tpl-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Pipeline</Label>
                  <select
                    value={editing.pipeline}
                    onChange={(e) => setEditing((f) => ({ ...f, pipeline: e.target.value }))}
                    className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
                    data-testid="wa-tpl-pipeline"
                  >
                    {PIPELINE_META.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Day (0-12)</Label>
                  <Input
                    type="number" min={0} max={12}
                    value={editing.sequence_step ?? ""}
                    onChange={(e) => setEditing((f) => ({ ...f, sequence_step: e.target.value === "" ? null : parseInt(e.target.value, 10) }))}
                    placeholder="Optional"
                    className="mt-1.5"
                    data-testid="wa-tpl-step"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Message body *</Label>
                <Textarea
                  value={editing.body}
                  onChange={(e) => setEditing((f) => ({ ...f, body: e.target.value }))}
                  rows={7}
                  placeholder="Hi [First Name], quick check-in from The Jay Project…"
                  className="mt-1.5 font-mono text-xs"
                  data-testid="wa-tpl-body"
                />
                <div className="mt-1 text-[10px] text-muted-foreground">
                  Placeholders: <code>[First Name]</code>, <code>[Company Name]</code>, <code>[City]</code>, <code>[Country]</code>, <code>[Industry]</code>. Reply keywords like "YES" auto-tag as hot; "STOP" auto-disqualifies.
                </div>
              </div>
            </div>
            <div className="mt-6 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
              <Button onClick={save} className="rounded-full gap-1.5" data-testid="wa-tpl-save">
                <Save className="h-3.5 w-3.5" /> {editing.id ? "Save" : "Create"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
